// Security Headers Utility
// Provides Content Security Policy and other security configurations

/**
 * Content Security Policy configuration
 * This helps prevent XSS attacks by controlling resource loading
 */
export const getCSPConfig = () => {
  const useLocal = process.env.REACT_APP_USE_LOCAL === 'true';
  const isLocalhost = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
  const isDevelopment = process.env.NODE_ENV === 'development';
  
  // Determine the correct API domain based on environment
  let apiDomain;
  if (useLocal || isLocalhost) {
    apiDomain = 'http://localhost:5000';
  } else {
    const baseURL = process.env.REACT_APP_API_BASE_URL || 'https://backend-neoeloi-neoelois-projects.vercel.app';
    apiDomain = new URL(baseURL).origin;
  }
  
  const csp = {
    'default-src': ["'self'"],
    'script-src': [
      "'self'",
      isDevelopment ? "'unsafe-eval'" : null, // Only allow eval in development
      "'unsafe-inline'", // For React inline scripts - consider removing and using nonces
    ].filter(Boolean),
    'style-src': [
      "'self'",
      "'unsafe-inline'", // For styled-components and CSS-in-JS
      'https://fonts.googleapis.com',
    ],
    'img-src': [
      "'self'",
      'data:', // For base64 images
      'blob:', // For dynamically generated images
      'https:', // Allow HTTPS images
    ],
    'font-src': [
      "'self'",
      'https://fonts.gstatic.com',
      'data:',
    ],
    'connect-src': [
      "'self'",
      apiDomain,
      'http://localhost:5000', // Local backend API
      'ws://localhost:*', // WebSocket for development
      'wss://*', // Secure WebSocket for production
    ],
    'media-src': ["'self'"],
    'object-src': ["'none'"], // Disable plugins
    'base-uri': ["'self'"],
    'form-action': ["'self'"],
    // Removed frame-ancestors as it cannot be set via meta tags
    'upgrade-insecure-requests': isDevelopment ? null : '', // Upgrade HTTP to HTTPS in production
  };

  // Convert to CSP string
  return Object.entries(csp)
    .filter(([, value]) => value !== null)
    .map(([key, value]) => `${key} ${Array.isArray(value) ? value.join(' ') : value}`)
    .join('; ');
};

/**
 * Security headers configuration for meta tags
 * Note: Some headers like X-Frame-Options and frame-ancestors can only be set via HTTP headers
 */
export const getSecurityHeaders = () => [
  {
    name: 'X-Content-Type-Options',
    content: 'nosniff'
  },
  // Removed X-Frame-Options as it cannot be set via meta tags
  {
    name: 'X-XSS-Protection',
    content: '1; mode=block'
  },
  {
    name: 'Referrer-Policy',
    content: 'strict-origin-when-cross-origin'
  },
  {
    name: 'Permissions-Policy',
    content: 'camera=(), microphone=(), geolocation=(), interest-cohort=()'
  }
];

/**
 * Apply security headers to the document
 */
export const applySecurityHeaders = () => {
  const headers = getSecurityHeaders();
  const csp = getCSPConfig();

  // Add CSP meta tag
  const cspMeta = document.createElement('meta');
  cspMeta.setAttribute('http-equiv', 'Content-Security-Policy');
  cspMeta.setAttribute('content', csp);
  document.head.appendChild(cspMeta);

  // Add other security headers
  headers.forEach(({ name, content }) => {
    const meta = document.createElement('meta');
    meta.setAttribute('http-equiv', name);
    meta.setAttribute('content', content);
    document.head.appendChild(meta);
  });
};

/**
 * Check if current context is secure (HTTPS in production)
 */
export const isSecureContext = () => {
  if (process.env.NODE_ENV === 'development') {
    return true; // Allow HTTP in development
  }
  
  return (
    window.location.protocol === 'https:' ||
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1'
  );
};

/**
 * Security warnings for insecure contexts
 */
export const checkSecurityContext = () => {
  if (!isSecureContext()) {
    console.warn(
      'Application is running in an insecure context. ' +
      'Some security features may not be available. ' +
      'Please use HTTPS in production.'
    );
    return false;
  }
  return true;
};

export default {
  getCSPConfig,
  getSecurityHeaders,
  applySecurityHeaders,
  isSecureContext,
  checkSecurityContext
}; 